/**
 * Does the name of an item fit the line? (decision D7, 19 Oct 2026)
 *
 * A sales person fixed seven picture lines with "Pick item" and picked "Nipple S 1/4-1/4
 * ZN M" for the line "Quick exh. valve G1/4": nothing on the dialog said that the two
 * names have no word in common. The pick dialog now shows a quiet warning on such an
 * item; the pick still saves.
 *
 * The test is deliberately loose, so it warns only when the names really differ: the
 * meaningful words are the words of three letters or more without a digit, minus units
 * and filler words. Numbers ("10008503", "G1/4", "M12", "0,75mm") and one- or two-letter
 * tokens ("S", "ZN") never count. Two words match when they are equal or when the shorter
 * one (three letters or more) starts the longer one ("exh" and "exhaust", "valve" and
 * "valves"). Without a meaningful word on either side there is nothing to compare, and
 * no warning.
 *
 * Pure functions, no React, so the rule is tested in `tests/picture-lines-ui.test.mjs`.
 */

/** Words that carry no meaning about the item: units, quantities and filler words. */
const NOT_MEANINGFUL = new Set([
  // units and quantities
  "bar",
  "psi",
  "kpa",
  "mpa",
  "rpm",
  "vac",
  "vdc",
  "pcs",
  "pce",
  "stk",
  "mtr",
  "inch",
  "set",
  // filler words of item texts and mails
  "and",
  "for",
  "the",
  "with",
  "from",
  "pos",
  "item",
  "type",
  "new",
]);

/** The shortest word that counts, and the shortest word that may start a longer one. */
const MIN_WORD = 3;

/** The meaningful words of a text, lower case, in their order, without repeats. */
export function meaningfulWords(text: string | null | undefined): string[] {
  const out: string[] = [];
  for (const token of (text ?? "").toLowerCase().split(/[^\p{L}\p{N}]+/u)) {
    if (token.length < MIN_WORD || /\p{N}/u.test(token) || NOT_MEANINGFUL.has(token)) {
      continue;
    }
    if (!out.includes(token)) {
      out.push(token);
    }
  }
  return out;
}

/** Two words match: equal, or the shorter one starts the longer one. */
function wordsMatch(a: string, b: string): boolean {
  if (a === b) {
    return true;
  }
  const [short, long] = a.length <= b.length ? [a, b] : [b, a];
  return short.length >= MIN_WORD && long.startsWith(short);
}

/**
 * True when the item name shares no meaningful word with the line text. False when either
 * side has no meaningful word (a bare number, an empty name): then nothing is compared.
 */
export function itemNameDiffers(
  lineText: string | null | undefined,
  itemName: string | null | undefined,
): boolean {
  const line = meaningfulWords(lineText);
  const item = meaningfulWords(itemName);
  if (line.length === 0 || item.length === 0) {
    return false;
  }
  return !line.some((word) => item.some((other) => wordsMatch(word, other)));
}
